import React, { useState, useEffect } from 'react'
import no from '../../../Assets/Images/Login/no.svg'
import './generateSalesOrder.css'
import { Formik, Field, Form } from 'formik'
import { BoxArrowUp } from 'react-bootstrap-icons'

import { t } from 'i18next'
import renderGenericMultipleSelectForm from 'Components/GenericMultipleSelectForm'
import { useDispatch, useSelector } from 'react-redux'
import { CircularProgress } from '@mui/material'
import { validateRoles, sanitizeOrderNumber } from 'Utils/functions'
import { salesRoles, EMPTY_GUID } from 'Utils/constants'
import { useCreditAccountValidation } from 'Hooks/useCreditAccountValidation'
import { creditAccountsAction } from 'redux/actions/catalogActions'

const GenerateSalesOrder = ({
  setShowModalGenerateSalesOrder,
  processSalesOrder,
  originalCreditAccount,
  initialOrderNumber = '',
  paymentSelectionMode = false,
  // customerId/companyId son opcionales: solo los pasan llamadores (ej. el
  // botón "Seleccionar método de pago" de una ODV ya creada) que abren este
  // modal sin que un flujo previo ya haya poblado state.catalog.creditAccountsResponse
  // para el cliente correcto. Cuando se pasan, el modal hace su propio fetch y
  // no permite seleccionar cuenta hasta que esa respuesta llegue, evitando
  // ofrecer cuentas obsoletas de otro cliente. Si no se pasan (flujo de
  // cotización existente), el comportamiento es exactamente el de antes.
  customerId,
  companyId,
}) => {
  const dispatch = useDispatch()
  const [showCreditExtraFields, setShowCreditExtraFields] = useState(
    !paymentSelectionMode
  )
  const [errorCreditAccount, setErrorCreditAccount] = useState(null)
  const [errorOrderNumber, setErrorOrderNumber] = useState(null)
  const [saveErrorMessage, setSaveErrorMessage] = useState(null)
  const [file, setFile] = useState(null)
  const [fileName, setFileName] = useState('')
  const [showComercialDropdown, setShowComercialDropdown] = useState(false)
  const [accountSelectedState, setAccountSelectedState] = useState('')
  const [loading, setLoading] = useState(false)
  const [accountsReady, setAccountsReady] = useState(!(customerId && companyId))
  // When customerId/companyId are provided, use local state to avoid stale data
  // from the global Redux state that could be overwritten by other flows.
  const [localCreditAccounts, setLocalCreditAccounts] = useState(null)
  const globalCreditAccounts = useSelector(
    state => state.catalog.creditAccountsResponse
  )
  const creditAccountsResponse =
    customerId && companyId ? localCreditAccounts : globalCreditAccounts

  const loginInfo = useSelector(state => state.login?.user?.AuthenticationInfo)
  const hasCustomerServiceRol = validateRoles(
    loginInfo.UserRoles,
    salesRoles.customerService
  )

  const isCreditAccountLocked =
    originalCreditAccount?.CreditAccountId &&
    originalCreditAccount.CreditAccountId !== EMPTY_GUID

  const {
    isLoading: loadingAccountValidation,
    blockMessage: creditBlockMessage,
  } = useCreditAccountValidation(accountSelectedState || null)

  const creditAccountsFetchFailed = creditAccountsResponse?.message === 'ERROR'
  const availableCreditAccounts =
    creditAccountsResponse?.Data?.filter(
      account => account.CreditAccountId !== EMPTY_GUID
    ) || []
  const clientHasCreditAccount = availableCreditAccounts.length > 0

  const disableSubmitButton = showCreditExtraFields
    ? loadingAccountValidation ||
      (!isCreditAccountLocked && (!accountsReady || !clientHasCreditAccount))
    : false

  const closeModalConfirmStartSalesOrder = () => {
    setShowModalGenerateSalesOrder(false)
  }

  const handleCreditRadioSelected = (isCredit, values) => {
    if (isCreditAccountLocked) return

    if (!isCredit) {
      values.orderNumber = ''
      setFile(null)
    }

    setShowCreditExtraFields(isCredit)
  }

  const onChangeFile = e => {
    const file = e.target.files[0]
    setFileName(file.name)
    setFile(file)
  }

  function validateCreditAccount(value, { skipListValidation = false } = {}) {
    // First validate the value itself before checking the available list.
    // This allows locked accounts to pass validation even if the list fetch failed.
    if (value === null || value === '') {
      setErrorCreditAccount(t('GENERATE_SALES_ORDER_T.REQUIRED_FIELD'))
      return false
    }

    if (value?.CreditAccountId === EMPTY_GUID) {
      setErrorCreditAccount(t('GENERATE_SALES_ORDER_T.INVALID_CREDIT_ACCOUNT'))
      return false
    }

    // Skip list validation when the account is locked (already assigned to the order)
    if (!skipListValidation && !clientHasCreditAccount) {
      setErrorCreditAccount(
        t(
          creditAccountsFetchFailed
            ? 'GENERATE_SALES_ORDER_T.CREDIT_ACCOUNTS_FETCH_ERROR'
            : 'GENERATE_SALES_ORDER_T.NO_ACTIVE_CREDIT_ACCOUNTS'
        )
      )
      return false
    }

    setErrorCreditAccount(null)
    return true
  }

  function validateOrderNumber(value) {
    const sanitized = sanitizeOrderNumber(value)
    if (!sanitized) {
      setErrorOrderNumber(t('GENERATE_SALES_ORDER_T.REQUIRED_FIELD'))
      return false
    }
    if (sanitized !== value) {
      setErrorOrderNumber(t('GENERATE_SALES_ORDER_T.INVALID_ORDER_NUMBER'))
      return false
    }
    setErrorOrderNumber(null)
    return true
  }

  const validateSalesOrder = async values => {
    let isValid = true

    if (showCreditExtraFields) {
      values.file = file

      if (isCreditAccountLocked) {
        values.orderNumber = sanitizeOrderNumber(values.orderNumber)
        isValid =
          isValid &&
          validateOrderNumber(values.orderNumber) &&
          validateCreditAccount(originalCreditAccount, {
            skipListValidation: true,
          })
      } else {
        const isCreditAccountValid =
          validateCreditAccount(accountSelectedState) &&
          accountSelectedState !== null
        const isOrderNumberValid = validateOrderNumber(values.orderNumber)
        isValid = isCreditAccountValid && isOrderNumberValid
      }
    }
    if (!isValid) return

    setSaveErrorMessage(null)
    setLoading(true)
    try {
      await processSalesOrder({
        ...values,
        accountItemSelected: isCreditAccountLocked
          ? originalCreditAccount
          : accountSelectedState,
      })
    } catch (error) {
      console.error('Error processing sales order', error)
      const errorMessage = error?.response?.data?.Message || error?.message
      setSaveErrorMessage(
        errorMessage || t('GENERATE_SALES_ORDER_T.SAVE_ERROR')
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isCreditAccountLocked) {
      setAccountSelectedState(originalCreditAccount)
      setShowCreditExtraFields(true)
    } else if (hasCustomerServiceRol || paymentSelectionMode) {
      handleCreditRadioSelected(false, { credit: 'no' })
    }
  }, [hasCustomerServiceRol, isCreditAccountLocked])

  useEffect(() => {
    let cancelled = false
    if (!customerId || !companyId) return

    setAccountsReady(false)
    setLocalCreditAccounts(null)
    dispatch(creditAccountsAction({ customerId, companyId }))
      .then(response => {
        if (!cancelled) {
          setLocalCreditAccounts(response)
        }
      })
      .finally(() => {
        if (!cancelled) setAccountsReady(true)
      })

    return () => {
      cancelled = true
    }
  }, [customerId, companyId])

  return (
    <div
      className="modal-box-generate-sales-order"
      data-testid="modal-box-generate-sales-order">
      <div className="modal-box-generate-sales-order-container">
        <div className="confirmation-header">
          <span
            onClick={closeModalConfirmStartSalesOrder}
            className="close-modal"
            data-testid="btn-close-generate-sales-order">
            <img src={no} alt="close" />
          </span>
        </div>
        <div>
          <Formik
            initialValues={{
              credit:
                (hasCustomerServiceRol || paymentSelectionMode) &&
                !isCreditAccountLocked
                  ? 'no'
                  : 'yes',
              direct: '',
              orderNumber: initialOrderNumber || '',
              file: '',
            }}
            onSubmit={async values => {
              await validateSalesOrder(values)
            }}>
            {({ values }) => (
              <Form>
                <div id="radio-credit" className="form-field-title">
                  {paymentSelectionMode
                    ? t('GENERATE_SALES_ORDER_T.PAYMENT_METHOD_LABEL')
                    : t('GENERATE_SALES_ORDER_T.COMERCIAL_CREDIT_LABEL')}
                </div>
                <div
                  role="group"
                  aria-labelledby="radio-credit"
                  className="payment-method-options mt-2"
                  data-testid="container-payment-method-options">
                  <label
                    className="form-field-option"
                    data-testid="text-radio-credit">
                    <Field
                      type="radio"
                      name="credit"
                      value="yes"
                      disabled={
                        (!paymentSelectionMode && hasCustomerServiceRol) ||
                        isCreditAccountLocked
                      }
                      checked={isCreditAccountLocked ? true : undefined}
                      onClick={() => handleCreditRadioSelected(true, values)}
                      data-testid="input-radio-credit"
                    />
                    <span>
                      {paymentSelectionMode
                        ? t('GENERATE_SALES_ORDER_T.COMMERCIAL_CREDIT_OPTION')
                        : t('GENERATE_SALES_ORDER_T.YES_OPTION_LABEL')}
                    </span>
                  </label>
                  <label
                    className="form-field-option"
                    data-testid="text-radio-cash">
                    <Field
                      type="radio"
                      name="credit"
                      value="no"
                      disabled={isCreditAccountLocked}
                      onClick={() => handleCreditRadioSelected(false, values)}
                      data-testid="input-radio-cash"
                    />
                    <span>
                      {paymentSelectionMode
                        ? t('GENERATE_SALES_ORDER_T.CASH_PAYMENT_OPTION')
                        : t('GENERATE_SALES_ORDER_T.NO_OPTION_LABEL')}
                    </span>
                  </label>
                </div>
                {showCreditExtraFields && (
                  <div
                    id="credit-extra-fields"
                    className="credit-extra-fields"
                    data-testid="container-credit-extra-fields">
                    {isCreditAccountLocked ? (
                      <div className="form-field-title">
                        {t('GENERATE_SALES_ORDER_T.EFECCTIVE_ACCOUNT_TITLE')}
                        <div
                          className="generic-multiple-address-filter-content"
                          style={{ opacity: 0.6, pointerEvents: 'none' }}>
                          <h2 className="generic-multiple-address-label-text">
                            {originalCreditAccount.CreditAccountNumber +
                              ' - ' +
                              (originalCreditAccount.Description || '')}
                          </h2>
                        </div>
                        <div
                          className="field-info-msg"
                          style={{
                            color: '#6c757d',
                            fontSize: '12px',
                            marginTop: '4px',
                          }}>
                          {t('GENERATE_SALES_ORDER_T.CREDIT_ACCOUNT_LOCKED')}
                        </div>
                      </div>
                    ) : !accountsReady ? (
                      <div
                        className="field-error-msg"
                        style={{
                          color: '#666',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}>
                        <CircularProgress size={12} />
                        {t('GENERATE_SALES_ORDER_T.LOADING_ACCOUNTS')}
                      </div>
                    ) : (
                      <div
                        className={
                          errorCreditAccount ? 'form-field-control-error' : ''
                        }>
                        {renderGenericMultipleSelectForm(
                          t('GENERATE_SALES_ORDER_T.EFECCTIVE_ACCOUNT_TITLE'),
                          showComercialDropdown,
                          setShowComercialDropdown,
                          accountSelectedState,
                          account => {
                            setAccountSelectedState(account)
                            setErrorCreditAccount(null)
                          },
                          availableCreditAccounts,
                          t(
                            'GENERATE_SALES_ORDER_T.EFECCTIVE_ACCOUNT_PLACEHOLDER'
                          ),
                          'DEL',
                          false,
                          'Description'
                        )}
                      </div>
                    )}
                    {!isCreditAccountLocked &&
                    accountsReady &&
                    ((errorCreditAccount && errorCreditAccount !== '') ||
                      !clientHasCreditAccount) ? (
                      <div className="field-error-msg">
                        {creditAccountsFetchFailed
                          ? t(
                              'GENERATE_SALES_ORDER_T.CREDIT_ACCOUNTS_FETCH_ERROR'
                            )
                          : !clientHasCreditAccount
                          ? t(
                              'GENERATE_SALES_ORDER_T.NO_ACTIVE_CREDIT_ACCOUNTS'
                            )
                          : errorCreditAccount}
                      </div>
                    ) : null}
                    {loadingAccountValidation && (
                      <div
                        className="field-error-msg"
                        style={{
                          color: '#666',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}>
                        <CircularProgress size={12} />
                        {t('GENERATE_SALES_ORDER_T.VALIDATING_ACCOUNT')}
                      </div>
                    )}
                    {!loadingAccountValidation && creditBlockMessage && (
                      <div className="field-error-msg" style={{ color: 'red' }}>
                        {creditBlockMessage}
                      </div>
                    )}
                    <div className="form-field-title mt-4">
                      <label htmlFor="order-number">
                        {t('GENERATE_SALES_ORDER_T.BUY_ORDER_LABEL')}
                      </label>
                      <div>
                        <Field
                          data-testid="input-order-number"
                          className={`form-field-text-input ${
                            errorOrderNumber ? 'form-field-control-error' : ''
                          }`}
                          id="order-number"
                          name="orderNumber"
                          placeholder={t(
                            'GENERATE_SALES_ORDER_T.SET_BUY_ORDER_PLACEHOLDER'
                          )}
                          value={values.orderNumber}
                          onBlur={event => {
                            const originalValue = event.target.value
                            const sanitized = sanitizeOrderNumber(originalValue)
                            event.target.value = sanitized
                            values.orderNumber = sanitized
                            validateOrderNumber(originalValue)
                          }}
                        />
                      </div>
                      {errorOrderNumber && errorOrderNumber !== '' && (
                        <div className="field-error-msg">
                          {errorOrderNumber}
                        </div>
                      )}
                    </div>
                    <div className="form-field-title mt-4">
                      <label>
                        {t('GENERATE_SALES_ORDER_T.SET_BUY_ORDER_FILE_LABEL')}
                      </label>
                      <div
                        className="add-file"
                        data-testid="add-file-sales-order">
                        <label
                          htmlFor="sales-order-file-upload"
                          data-testid="label-file-upload-sales-order">
                          <BoxArrowUp />
                          {t(
                            'GENERATE_SALES_ORDER_T.SEARCH_FOR_DOCUMENT_LABEL'
                          )}
                        </label>
                        <input
                          data-testid="input-file-upload-sales-order"
                          id="sales-order-file-upload"
                          type="file"
                          style={{ display: 'none' }}
                          accept=".pdf,.png,.jpg,.jpeg"
                          onChange={onChangeFile}
                        />
                        {fileName ? (
                          <div
                            className="add-file-label"
                            data-testid="file-name-sales-order">
                            {fileName}
                          </div>
                        ) : (
                          <div
                            className="add-file-label-placeholder"
                            data-testid="file-name-placeholder-sales-order">
                            {t(
                              'GENERATE_SALES_ORDER_T.FILE_NOT_SELECTED_LABEL'
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {saveErrorMessage && (
                  <div
                    className="field-error-msg mt-4"
                    data-testid="error-message-sales-order">
                    {saveErrorMessage}
                  </div>
                )}

                <div className="modal-buttons">
                  <button
                    data-testid="btn-continue-sales-order"
                    type="submit"
                    className="btn modal-si"
                    disabled={disableSubmitButton || loading}>
                    {loading ? (
                      <CircularProgress
                        sx={{
                          '& .MuiCircularProgress-svg': { color: '#FFFFFF' },
                        }}
                        size={18}
                        style={{ marginTop: '5px' }}
                      />
                    ) : (
                      t('GENERATE_SALES_ORDER_T.CONTINUE_BUTTON_LABEL')
                    )}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  )
}

export default GenerateSalesOrder
